import moment from 'moment';
import { DataSet } from '@facet-wealth/design';

const coneLowSeriesName = 'total-account-balance-all-tolerance-low';
const coneHighSeriesName = 'total-account-balance-all-tolerance-high';
const coneSeries = [coneLowSeriesName, coneHighSeriesName];
const isConeSeries = (seriesName) => coneSeries.includes(seriesName);

const filterOutcomesBySchema = (outcomes, schemaID) => {
  if (!outcomes) {
    return outcomes;
  }
  return outcomes.filter(outcome => !outcome.attributes.dataSchemaId || outcome.attributes.dataSchemaId.includes(schemaID));
};

const goalFundingZoneToStatus = (fundingZone) => {
  switch (fundingZone) {
    case 'Green':
      return 'success';
    case 'Yellow':
      return 'warning';
    case 'Red':
      return 'error';
    default:
      return 'error';
  }
};

export const convertChartDataResourceToDataSet = (
  chartDataResource,
  selectedDataSetId,
  chartDataSourceIdsToShow = null,
  yAxisLabel = 'NET FINANCIAL RESOURCES',
  xAxisLabel = 'YOUR TIMELINE',
) => {
  const exportedChartDataResource = chartDataResource.export();

  let desiredChartDataSourceIDs = chartDataSourceIdsToShow;
  if ((desiredChartDataSourceIDs == null || desiredChartDataSourceIDs?.length === 0) && selectedDataSetId != null) {
    desiredChartDataSourceIDs = [selectedDataSetId];
  }
  let schemasToShow = exportedChartDataResource.attributes.dataSchema.data;
  const sourceIDToColor = Object.fromEntries(schemasToShow.map((schema) => ([schema.attributes.chartDataSourceId, schema.attributes.color])));
  if (desiredChartDataSourceIDs != null) {
    schemasToShow = schemasToShow.filter((schemaResource) => {
      return desiredChartDataSourceIDs.includes(schemaResource.attributes.chartDataSourceId);
    });
  }

  const dataSeries = schemasToShow.map((schemaResource) => {
    const firstPoint = exportedChartDataResource.attributes?.data?.data[0];
    const firstKey = firstPoint?.attributes?.keys?.data[0];
    const dataFormat = firstKey?.attributes?.valueFormat ?? 'usdAvoidNegative';
    // A balance schema's ID will either be "balance" or something like "123_balance" where 123 is the plan's ID when comparing plans
    const isBalanceSeries = /(^|_)balance/.test(schemaResource.id);

    const isSelected = selectedDataSetId === schemaResource.attributes.chartDataSourceId;
    return {
      referenceName: schemaResource.id,
      showEmphasizedItems: isSelected,
      color: sourceIDToColor[schemaResource.attributes.chartDataSourceId],
      name: schemaResource.attributes.label,
      representation: (schemaResource.attributes.presentation === 'bar') ? 'stacked-bar' : schemaResource.attributes.presentation,
      dataPointFormats: ['Age', dataFormat],
      groupName: 'group-1',
      points: exportedChartDataResource.attributes.data.data.map((dataResource) => {
        const pointResource = dataResource.attributes.keys.data.find((currPointRes) => {
          return currPointRes.id === schemaResource.id;
        });

        // this happens when there is no more data points in the series (one series is longer than the other)
        if (!pointResource) {
          return undefined;
        }


        // Only show outcomes on the balance series for now
        const canHaveOutcomes = isBalanceSeries;
        const outcomes = canHaveOutcomes ? filterOutcomesBySchema(dataResource.attributes.outcomes?.data, schemaResource.id) : [];
        const hasOutcomes = outcomes.length > 0;

        return {
          x: moment(dataResource.id).toDate(),
          y: Number(pointResource.attributes.value),
          meta: {
            emphasized: isSelected && hasOutcomes,
            emphasizedItems: outcomes.map(outcome => {
              return {
                icon: outcome.attributes.icon,
                status: goalFundingZoneToStatus(outcome.attributes.fundingZone),
                meta: {
                  name: outcome.attributes.titleWithYear || outcome.attributes.title,
                  amountDescription: outcome.attributes.amountDescription,
                  fundedPercentDescription: outcome.attributes.fundedPercentDescription,
                },
              };
            }),
            clients: dataResource.attributes.clients?.data || [],
          },
        };
      }).filter(item => item !== undefined),
    };
  });

  const hasConeSeries = exportedChartDataResource.attributes.dataSchema.data.filter((schemaResource) => {
    return isConeSeries(schemaResource.id);
  }).length === coneSeries.length;

  const dataRanges = (hasConeSeries) ? [{
    referenceName: 'balance-range',
    name: 'Likely Between',
    color: 'primary',
    series: [coneLowSeriesName, coneHighSeriesName],
  }] : undefined;

  return new DataSet({
    series: dataSeries,
    ranges: dataRanges,
    birthDates: exportedChartDataResource.attributes?.clientBirthDates?.map(date => {
      return moment(date).toDate();
    }) ?? [],
    yAxisLabel,
    xAxisLabel,
  });
};
